import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { getErrorDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function RegisterPage() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name:"", email:"", password:"", role:"student" });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault(); setLoading(true);
    try {
      const u = await register({ ...form, email: form.email.trim().toLowerCase() });
      toast.success(`Welcome, ${u.name || u.email}`);
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(getErrorDetail(err));
    } finally { setLoading(false); }
  }

  return (
    <main data-testid="register-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 md:p-16">
      <form onSubmit={submit} className="w-full max-w-md space-y-6 bg-white p-8 rounded-2xl border border-[hsl(var(--border))]" data-testid="register-form">
        <Link to="/" className="inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"><ArrowLeft className="w-3 h-3" />{t.common.back_home}</Link>
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium">{t.auth.register_title}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{t.auth.register_sub}</p>
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="r-name" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.auth.name}</Label>
            <Input id="r-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="register-name" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="r-email" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.auth.email}</Label>
            <Input id="r-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="register-email" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="r-pw" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.auth.password}</Label>
            <Input id="r-pw" type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} data-testid="register-password" className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.auth.role}</Label>
            <RadioGroup value={form.role} onValueChange={(v)=>setForm({...form, role:v})} className="flex gap-4 mt-3" data-testid="register-role">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="student" data-testid="register-role-student" />{t.auth.role_student}</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="parent" data-testid="register-role-parent" />{t.auth.role_parent}</label>
            </RadioGroup>
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full">{loading ? "…" : t.auth.submit_register}</Button>
        <p className="text-sm text-center text-[hsl(var(--muted-foreground))]">
          <Link to="/login" data-testid="register-to-login" className="hover:text-[hsl(var(--primary))]">{t.auth.switch_to_login}</Link>
        </p>
      </form>
    </main>
  );
}
